/**
 * RiskIssueDrawers — Shared drawer components for risk and issue detail.
 *
 * Extracted from RisksIssuesTab for reuse on the Summary tab and
 * any future surface that needs drill-down into risk/issue detail.
 *
 * Both drawers support a `readOnly` prop that hides all mutation controls
 * (edit, save, delete, materialize) regardless of canEdit. This allows
 * portfolio managers on the Summary tab to drill in without edit rights.
 *
 * TODO (DEV-738 cutover): Replace canEdit (V1 canEditProjectContent) with
 * hasCapability('risks:edit') and gate materialize behind
 * hasCapability('risks:materialize'). See ADR-001-permissions-system.md.
 *
 * Epic: DEV-683
 */

import React from 'react';
import type { ProjectRiskResponse } from '../../api/projectRisks';
import type { ProjectIssueResponse } from '../../api/projectIssues';
import type { ProjectRoleSlotResponse } from '../../api/projectRoles';
import { riskScoreColor, riskScoreBgColor } from './riskScoreColor';
import styles from './RisksIssuesTab.module.css';

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** L/I button tint colors (10% opacity for unselected, subtle). */
const LI_TINTS: Record<number, string> = {
  1: 'rgba(76, 175, 80, 0.10)',
  2: 'rgba(139, 195, 74, 0.10)',
  3: 'rgba(255, 193, 7, 0.10)',
  4: 'rgba(255, 152, 0, 0.10)',
  5: 'rgba(244, 67, 54, 0.10)',
};

const LI_COLORS: Record<number, string> = {
  1: '#4CAF50',
  2: '#8BC34A',
  3: '#FFC107',
  4: '#FF9800',
  5: '#F44336',
};

export const RISK_STATUSES = ['identified', 'mitigating', 'accepted', 'materialized', 'closed'];
// Category values MUST match ProjectRisk.ValidCategories on the backend.
// Display labels live in i18n (projectDetail.risksIssues.category_<value>).
// Bug fix DEV-822 spillover: frontend used 'safety' but backend uses
// 'health_safety' — caused 400 "Invalid category" on PATCH/save.
export const RISK_CATEGORIES = [
  'technical', 'operational', 'financial',
  'environmental', 'regulatory', 'reputational', 'health_safety',
];

export const ISSUE_STATUSES = ['open', 'in_progress', 'resolved', 'closed'];
export const ISSUE_SEVERITIES = ['blocking', 'warning', 'informational'];
// Category values MUST match ProjectIssue.ValidCategories on the backend
// (which is shared with ProjectRisk.ValidCategories).
export const ISSUE_CATEGORIES = [
  'operational', 'technical', 'financial',
  'environmental', 'regulatory', 'health_safety',
];

/* ------------------------------------------------------------------ */
/*  RiskScoreBar                                                       */
/* ------------------------------------------------------------------ */

export function RiskScoreBar({ score, compact }: { score: number; compact?: boolean }) {
  const pct = Math.min(100, (score / 25) * 100);
  const color = riskScoreColor(score);
  return (
    <span className={compact ? styles.scoreBarCompact : styles.scoreBar}>
      <span className={styles.scoreBarTrack}>
        <span
          className={styles.scoreBarFill}
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </span>
      <span className={styles.scoreBarLabel} style={{ color }}>
        {score}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Assessment Panel — Likelihood × Impact with color-coded buttons    */
/* ------------------------------------------------------------------ */

export function AssessmentPanel({
  likelihood,
  impact,
  editing,
  onLikelihoodChange,
  onImpactChange,
  t,
}: {
  likelihood: number;
  impact: number;
  editing: boolean;
  onLikelihoodChange: (v: number) => void;
  onImpactChange: (v: number) => void;
  t: (key: string) => string;
}) {
  const score = likelihood * impact;
  return (
    <div className={styles.assessmentPanel}>
      <div className={styles.assessmentRow}>
        <span className={styles.assessmentLabel}>
          {t('projectDetail.risksIssues.likelihood')}
        </span>
        <div className={styles.liButtons}>
          {[1, 2, 3, 4, 5].map((v) => (
            <button
              key={v}
              type="button"
              disabled={!editing}
              className={`${styles.liBtn} ${likelihood === v ? styles.liBtnSelected : ''}`}
              style={
                likelihood === v
                  ? { backgroundColor: LI_COLORS[v], color: '#fff' }
                  : { backgroundColor: LI_TINTS[v] }
              }
              onClick={() => onLikelihoodChange(v)}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.assessmentRow}>
        <span className={styles.assessmentLabel}>
          {t('projectDetail.risksIssues.impact')}
        </span>
        <div className={styles.liButtons}>
          {[1, 2, 3, 4, 5].map((v) => (
            <button
              key={v}
              type="button"
              disabled={!editing}
              className={`${styles.liBtn} ${impact === v ? styles.liBtnSelected : ''}`}
              style={
                impact === v
                  ? { backgroundColor: LI_COLORS[v], color: '#fff' }
                  : { backgroundColor: LI_TINTS[v] }
              }
              onClick={() => onImpactChange(v)}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.assessmentScoreRow}>
        <span className={styles.assessmentLabel}>
          {t('projectDetail.risksIssues.riskScore')}
        </span>
        <RiskScoreBar score={score} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Risk Detail Drawer                                                 */
/* ------------------------------------------------------------------ */

export interface RiskDrawerProps {
  risk: ProjectRiskResponse;
  editing: boolean;
  setEditing: (v: boolean) => void;
  draft: Partial<ProjectRiskResponse>;
  setDraft: React.Dispatch<React.SetStateAction<Partial<ProjectRiskResponse>>>;
  onSave: () => void;
  onClose: () => void;
  onDelete: () => void;
  onMaterialize: () => void;
  /** V1 permission: canEditProjectContent. TODO (DEV-738): replace with hasCapability('risks:edit'). */
  canEdit: boolean;
  /** When true, hides all edit/delete/materialize controls regardless of canEdit. */
  readOnly?: boolean;
  busy: boolean;
  resolveUser: (g: string | null | undefined) => string;
  resolveRoleSlot: (g: string | null | undefined) => string;
  roleSlots: ProjectRoleSlotResponse[];
  t: (key: string) => string;
}

export function RiskDrawer({
  risk,
  editing,
  setEditing,
  draft,
  setDraft,
  onSave,
  onClose,
  onDelete,
  onMaterialize,
  canEdit,
  readOnly = false,
  busy,
  resolveUser,
  resolveRoleSlot,
  roleSlots,
  t,
}: RiskDrawerProps) {
  const effectiveCanEdit = canEdit && !readOnly;
  const riskData = editing ? { ...risk, ...draft } : risk;
  const score = (riskData.likelihood ?? 1) * (riskData.impact ?? 1);

  return (
    <>
      <div className={styles.drawerBackdrop} onClick={onClose} />
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <h3 className={styles.drawerTitle}>
            {editing ? (
              <input
                className={styles.drawerTitleInput}
                value={draft.title ?? risk.title}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, title: e.target.value }))
                }
              />
            ) : (
              risk.title
            )}
          </h3>
          <div className={styles.drawerActions}>
            {effectiveCanEdit && !editing && (
              <button
                className={styles.drawerEditBtn}
                onClick={() => setEditing(true)}
              >
                {t('projectDetail.risksIssues.edit')}
              </button>
            )}
            {editing && (
              <>
                <button
                  className={styles.drawerSaveBtn}
                  onClick={onSave}
                  disabled={busy}
                >
                  {t('projectDetail.risksIssues.save')}
                </button>
                <button
                  className={styles.drawerCancelBtn}
                  onClick={() => {
                    setEditing(false);
                    setDraft({});
                  }}
                >
                  {t('projectDetail.risksIssues.cancel')}
                </button>
              </>
            )}
            <button className={styles.drawerCloseBtn} onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        <div className={styles.drawerBody}>
          {/* Assessment */}
          <AssessmentPanel
            likelihood={
              (editing ? (draft.likelihood ?? risk.likelihood) : risk.likelihood) ?? 1
            }
            impact={
              (editing ? (draft.impact ?? risk.impact) : risk.impact) ?? 1
            }
            editing={editing}
            onLikelihoodChange={(v) =>
              setDraft((p) => ({ ...p, likelihood: v }))
            }
            onImpactChange={(v) =>
              setDraft((p) => ({ ...p, impact: v }))
            }
            t={t}
          />

          {/* Status + Category */}
          <div className={styles.drawerFieldGrid}>
            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.status')}
            </label>
            {editing ? (
              <select
                className={styles.drawerSelect}
                value={draft.status ?? risk.status}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, status: e.target.value }))
                }
              >
                {RISK_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`projectDetail.risksIssues.riskStatus_${s}`)}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.drawerFieldValue}>
                {t(`projectDetail.risksIssues.riskStatus_${risk.status}`)}
              </span>
            )}

            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.category')}
            </label>
            {editing ? (
              <select
                className={styles.drawerSelect}
                value={draft.category ?? risk.category}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, category: e.target.value }))
                }
              >
                {RISK_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`projectDetail.risksIssues.category_${c}`)}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.drawerFieldValue}>
                {t(`projectDetail.risksIssues.category_${risk.category}`)}
              </span>
            )}

            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.owner')}
            </label>
            {editing ? (
              <select
                className={styles.drawerSelect}
                value={draft.ownerRoleSlotGuid ?? risk.ownerRoleSlotGuid ?? ''}
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    ownerRoleSlotGuid: e.target.value || null,
                  }))
                }
              >
                <option value="">
                  {t('projectDetail.risksIssues.selectRole')}
                </option>
                {roleSlots.map((slot) => (
                  <option key={slot.roleSlotGuid} value={slot.roleSlotGuid}>
                    {slot.roleLabel}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.drawerFieldValue}>
                {riskData.ownerRoleSlotGuid
                  ? resolveRoleSlot(riskData.ownerRoleSlotGuid)
                  : resolveUser(riskData.ownerUserGuid)}
              </span>
            )}

            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.keyRisk')}
            </label>
            {editing ? (
              <label className={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={draft.isKeyRisk ?? risk.isKeyRisk}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, isKeyRisk: e.target.checked }))
                  }
                />
                {t('projectDetail.risksIssues.keyRiskHint')}
              </label>
            ) : (
              <span className={styles.drawerFieldValue}>
                {risk.isKeyRisk
                  ? t('projectDetail.risksIssues.yes')
                  : t('projectDetail.risksIssues.no')}
              </span>
            )}
          </div>

          {/* Description */}
          <div className={styles.drawerSection}>
            <label className={styles.drawerSectionLabel}>
              {t('projectDetail.risksIssues.description')}
            </label>
            {editing ? (
              <textarea
                className={styles.drawerTextarea}
                rows={3}
                value={draft.description ?? risk.description ?? ''}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, description: e.target.value }))
                }
              />
            ) : (
              <p className={styles.drawerText}>
                {risk.description || '—'}
              </p>
            )}
          </div>

          {/* Mitigation Plan */}
          <div className={styles.drawerSection}>
            <label className={styles.drawerSectionLabel}>
              {t('projectDetail.risksIssues.mitigationPlan')}
            </label>
            {editing ? (
              <textarea
                className={styles.drawerTextarea}
                rows={3}
                value={draft.mitigationPlan ?? risk.mitigationPlan ?? ''}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, mitigationPlan: e.target.value }))
                }
              />
            ) : (
              <p className={styles.drawerText}>
                {risk.mitigationPlan || '—'}
              </p>
            )}
          </div>

          {/* Trigger Conditions */}
          <div className={styles.drawerSection}>
            <label className={styles.drawerSectionLabel}>
              {t('projectDetail.risksIssues.triggerConditions')}
            </label>
            {editing ? (
              <textarea
                className={styles.drawerTextarea}
                rows={2}
                value={draft.triggerConditions ?? risk.triggerConditions ?? ''}
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    triggerConditions: e.target.value,
                  }))
                }
              />
            ) : (
              <p className={styles.drawerText}>
                {risk.triggerConditions || '—'}
              </p>
            )}
          </div>

          {/* Meta */}
          <div className={styles.drawerMeta}>
            <span>
              {t('projectDetail.risksIssues.createdBy')}:{' '}
              {resolveUser(risk.createdBy)} · {formatDate(risk.createdAt)}
            </span>
          </div>

          {/* Footer actions — hidden in readOnly mode */}
          {effectiveCanEdit && !editing && (
            <div className={styles.drawerFooter}>
              {risk.status !== 'closed' && !risk.materializedIssueId && (
                <button
                  className={styles.materializeBtn}
                  onClick={onMaterialize}
                  disabled={busy}
                >
                  {t('projectDetail.risksIssues.materializeToIssue')}
                </button>
              )}
              <button
                className={styles.deleteBtn}
                onClick={onDelete}
                disabled={busy}
              >
                {t('projectDetail.risksIssues.delete')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Issue Detail Drawer                                                */
/* ------------------------------------------------------------------ */

export interface IssueDrawerProps {
  issue: ProjectIssueResponse;
  editing: boolean;
  setEditing: (v: boolean) => void;
  draft: Partial<ProjectIssueResponse>;
  setDraft: React.Dispatch<React.SetStateAction<Partial<ProjectIssueResponse>>>;
  onSave: () => void;
  onClose: () => void;
  onDelete: () => void;
  /** V1 permission: canEditProjectContent. TODO (DEV-738): replace with hasCapability('risks:edit'). */
  canEdit: boolean;
  /** When true, hides all edit/delete controls regardless of canEdit. */
  readOnly?: boolean;
  busy: boolean;
  resolveUser: (g: string | null | undefined) => string;
  resolveRoleSlot: (g: string | null | undefined) => string;
  roleSlots: ProjectRoleSlotResponse[];
  t: (key: string) => string;
}

export function IssueDrawer({
  issue,
  editing,
  setEditing,
  draft,
  setDraft,
  onSave,
  onClose,
  onDelete,
  canEdit,
  readOnly = false,
  busy,
  resolveUser,
  resolveRoleSlot,
  roleSlots,
  t,
}: IssueDrawerProps) {
  const effectiveCanEdit = canEdit && !readOnly;
  const issueData = editing ? { ...issue, ...draft } : issue;

  return (
    <>
      <div className={styles.drawerBackdrop} onClick={onClose} />
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <h3 className={styles.drawerTitle}>
            {editing ? (
              <input
                className={styles.drawerTitleInput}
                value={draft.title ?? issue.title}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, title: e.target.value }))
                }
              />
            ) : (
              issue.title
            )}
          </h3>
          <div className={styles.drawerActions}>
            {effectiveCanEdit && !editing && (
              <button
                className={styles.drawerEditBtn}
                onClick={() => setEditing(true)}
              >
                {t('projectDetail.risksIssues.edit')}
              </button>
            )}
            {editing && (
              <>
                <button
                  className={styles.drawerSaveBtn}
                  onClick={onSave}
                  disabled={busy}
                >
                  {t('projectDetail.risksIssues.save')}
                </button>
                <button
                  className={styles.drawerCancelBtn}
                  onClick={() => {
                    setEditing(false);
                    setDraft({});
                  }}
                >
                  {t('projectDetail.risksIssues.cancel')}
                </button>
              </>
            )}
            <button className={styles.drawerCloseBtn} onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        <div className={styles.drawerBody}>
          {/* Fields grid */}
          <div className={styles.drawerFieldGrid}>
            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.severity')}
            </label>
            {editing ? (
              <select
                className={styles.drawerSelect}
                value={draft.severity ?? issue.severity}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, severity: e.target.value }))
                }
              >
                {ISSUE_SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {t(`projectDetail.risksIssues.severity_${s}`)}
                  </option>
                ))}
              </select>
            ) : (
              <span
                className={`${styles.severityPill} ${
                  issue.severity === 'blocking'
                    ? styles.severityBlocking
                    : issue.severity === 'warning'
                    ? styles.severityWarning
                    : styles.severityInfo
                }`}
              >
                {t(`projectDetail.risksIssues.severity_${issue.severity}`)}
              </span>
            )}

            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.status')}
            </label>
            {editing ? (
              <select
                className={styles.drawerSelect}
                value={draft.status ?? issue.status}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, status: e.target.value }))
                }
              >
                {ISSUE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`projectDetail.risksIssues.issueStatus_${s}`)}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.drawerFieldValue}>
                {t(`projectDetail.risksIssues.issueStatus_${issue.status}`)}
              </span>
            )}

            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.category')}
            </label>
            {editing ? (
              <select
                className={styles.drawerSelect}
                value={draft.category ?? issue.category}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, category: e.target.value }))
                }
              >
                {ISSUE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`projectDetail.risksIssues.category_${c}`)}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.drawerFieldValue}>
                {t(`projectDetail.risksIssues.category_${issue.category}`)}
              </span>
            )}

            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.owner')}
            </label>
            {editing ? (
              <select
                className={styles.drawerSelect}
                value={draft.ownerRoleSlotGuid ?? issue.ownerRoleSlotGuid ?? ''}
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    ownerRoleSlotGuid: e.target.value || null,
                  }))
                }
              >
                <option value="">
                  {t('projectDetail.risksIssues.selectRole')}
                </option>
                {roleSlots.map((slot) => (
                  <option key={slot.roleSlotGuid} value={slot.roleSlotGuid}>
                    {slot.roleLabel}
                  </option>
                ))}
              </select>
            ) : (
              <span className={styles.drawerFieldValue}>
                {issueData.ownerRoleSlotGuid
                  ? resolveRoleSlot(issueData.ownerRoleSlotGuid)
                  : resolveUser(issueData.ownerUserGuid)}
              </span>
            )}

            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.dueDate')}
            </label>
            {editing ? (
              <input
                type="date"
                className={styles.drawerInput}
                value={draft.dueDate ?? issue.dueDate ?? ''}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, dueDate: e.target.value }))
                }
              />
            ) : (
              <span className={styles.drawerFieldValue}>
                {formatDate(issue.dueDate)}
              </span>
            )}

            <label className={styles.drawerFieldLabel}>
              {t('projectDetail.risksIssues.keyIssue')}
            </label>
            {editing ? (
              <label className={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={draft.isKeyIssue ?? issue.isKeyIssue}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, isKeyIssue: e.target.checked }))
                  }
                />
                {t('projectDetail.risksIssues.keyIssueHint')}
              </label>
            ) : (
              <span className={styles.drawerFieldValue}>
                {issue.isKeyIssue
                  ? t('projectDetail.risksIssues.yes')
                  : t('projectDetail.risksIssues.no')}
              </span>
            )}
          </div>

          {/* Description */}
          <div className={styles.drawerSection}>
            <label className={styles.drawerSectionLabel}>
              {t('projectDetail.risksIssues.description')}
            </label>
            {editing ? (
              <textarea
                className={styles.drawerTextarea}
                rows={3}
                value={draft.description ?? issue.description ?? ''}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, description: e.target.value }))
                }
              />
            ) : (
              <p className={styles.drawerText}>
                {issue.description || '—'}
              </p>
            )}
          </div>

          {/* Resolution Plan */}
          <div className={styles.drawerSection}>
            <label className={styles.drawerSectionLabel}>
              {t('projectDetail.risksIssues.resolutionPlan')}
            </label>
            {editing ? (
              <textarea
                className={styles.drawerTextarea}
                rows={3}
                value={draft.resolutionPlan ?? issue.resolutionPlan ?? ''}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, resolutionPlan: e.target.value }))
                }
              />
            ) : (
              <p className={styles.drawerText}>
                {issue.resolutionPlan || '—'}
              </p>
            )}
          </div>

          {/* Meta */}
          <div className={styles.drawerMeta}>
            <span>
              {t('projectDetail.risksIssues.createdBy')}:{' '}
              {resolveUser(issue.createdBy)} · {formatDate(issue.createdAt)}
            </span>
            {issue.sourceRiskId && (
              <span className={styles.sourceTag}>
                {t('projectDetail.risksIssues.materializedFromRisk')}
              </span>
            )}
            {issue.sourceFlagId && !issue.sourceRiskId && (
              <span className={styles.sourceTag}>
                {t('projectDetail.risksIssues.promotedFromFlag')}
              </span>
            )}
          </div>

          {/* Footer actions — hidden in readOnly mode */}
          {effectiveCanEdit && !editing && (
            <div className={styles.drawerFooter}>
              <button
                className={styles.deleteBtn}
                onClick={onDelete}
                disabled={busy}
              >
                {t('projectDetail.risksIssues.delete')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
